/**
 * @mentions in discussion answers (SoundGuys team in discussions, 2026-10-09): the token rule,
 * the reply body's runs (links, then mentions), the composer's caret logic and its picker rows,
 * and the projection of DealHunt's `/mentionables` read. Pure and client-safe — no fetch, no
 * React; unit-tested in `tests/community-discussions-mentions.spec.ts`.
 *
 * A mention is PLAIN TEXT in the stored body: `@sg-team` asks the team, `@<handle>` names a
 * member. commentHub and DealHunt extract the tokens server-side with the same regex as
 * `MENTION_RE` (contract "Vocabulary"), so what the reader sees highlighted is what is notified.
 * Nothing here decides who is notified; a highlight is drawn only for the team token and for a
 * handle the page already knows (mentionables, the thread's team profiles, the asker).
 */
import { avatarInitials, knownTier, memberHandleParam, tierLabel } from './model'
import type {
  DiscussionMentionable,
  DiscussionMentionables,
  DiscussionStaffOfThread,
} from './discussions-types'
import { withSlash } from 'lib/dealhunt/url'
import { TEAM_PILL, linkRuns } from './discussions'

/** The literal team token in a body (without the `@`). */
export const TEAM_TOKEN = 'sg-team'
/** Reader-facing name of the team — never "staff" (the pill's text, one string). */
export const TEAM_LABEL = TEAM_PILL
/** The composer's hint under the field. */
export const MENTION_HINT = 'type @ to mention someone or the team'
/** DealHunt keeps at most this many distinct tokens per body (contract). */
export const MENTIONS_MAX = 10
/** DealHunt sends at most this many members. */
export const MENTIONABLES_MAX = 30

/** The contract's mention rule, verbatim: a `@` not preceded by a word character or another `@`. */
const MENTION_RE = /(^|[^\w@])@([a-z0-9][a-z0-9_.-]{1,39})/gi

export interface MentionMatch {
  /** Index of the `@`. */
  start: number
  /** Index after the kept token (a trailing `.` / `-` is not part of it). */
  end: number
  /** Lowercased, without the `@`. */
  token: string
}

/** The mention tokens of a text, in order — at most `MENTIONS_MAX` distinct ones (a later
 *  token beyond the cap is not a mention, as on the server). */
export function mentionMatches(text: string): MentionMatch[] {
  const out: MentionMatch[] = []
  const distinct = new Set<string>()
  for (const m of Array.from(text.matchAll(MENTION_RE))) {
    const at = (m.index ?? 0) + m[1].length
    const kept = m[2].replace(/[.-]+$/, '')
    if (!kept) {
      continue
    }
    const token = kept.toLowerCase()
    if (!distinct.has(token)) {
      if (distinct.size >= MENTIONS_MAX) {
        continue
      }
      distinct.add(token)
    }
    out.push({ start: at, end: at + 1 + kept.length, token })
  }
  return out
}

export type MentionSegment = { text: string } | { text: string; token: string }

/** A text cut into plain runs and known mention tokens. `isKnown` decides a member handle; the
 *  team token is always one. An unknown `@word` stays plain text. */
export function mentionSegments(
  text: string,
  isKnown: (token: string) => boolean
): MentionSegment[] {
  const out: MentionSegment[] = []
  let last = 0
  for (const match of mentionMatches(text)) {
    if (match.token !== TEAM_TOKEN && !isKnown(match.token)) {
      continue
    }
    if (match.start > last) {
      out.push({ text: text.slice(last, match.start) })
    }
    out.push({ text: text.slice(match.start, match.end), token: match.token })
    last = match.end
  }
  if (last < text.length) {
    out.push({ text: text.slice(last) })
  }
  return out
}

export type ReplyRun =
  | { text: string }
  | { text: string; href: string }
  | { text: string; token: string }

/** One line of a reply: http(s) links first (a URL may hold an `@`, `medium.com/@user`), then
 *  the mentions in the text between them. */
export function replyRuns(line: string, isKnown: (token: string) => boolean): ReplyRun[] {
  return linkRuns(line).flatMap<ReplyRun>(run => {
    if ('href' in run) {
      return [run]
    }
    return mentionSegments(run.text, isKnown)
  })
}

/** How a token reads in a body: "@SoundGuys team" for the team, the typed text otherwise. */
export function mentionText(segment: { text: string; token: string }): string {
  return segment.token === TEAM_TOKEN ? `@${TEAM_LABEL}` : segment.text
}

/**
 * The handles a body may highlight and link: the mentionables' members, the thread's SoundGuys
 * team profiles and any extra handle the page knows (the asker), all lowercased. The owner map
 * carries no handles, so owners are known only through these.
 */
export function knownHandles(
  mentionables: DiscussionMentionables | null | undefined,
  staff: Record<string, DiscussionStaffOfThread> | null | undefined,
  extra: readonly (string | null | undefined)[] = []
): Set<string> {
  const known = new Set<string>()
  const add = (raw: unknown): void => {
    const handle = memberHandleParam(raw)
    if (handle) {
      known.add(handle)
    }
  }
  mentionables?.members.forEach(m => add(m.handle))
  Object.values(staff ?? {}).forEach(s => add(s?.handle))
  extra.forEach(add)
  return known
}

// ── the composer ─────────────────────────────────────────────────────────────

/** The mention being typed at the caret: the `@`'s index and the (lowercased) text after it, or
 *  null when the caret is not in one. `@` must start the text or follow a non-word character. */
export function activeMention(
  text: string,
  caret: number
): { start: number; query: string } | null {
  const before = text.slice(0, Math.max(0, Math.min(caret, text.length)))
  const m = /(^|[^\w@])@([a-z0-9_.-]{0,39})$/i.exec(before)
  if (!m) {
    return null
  }
  return { start: before.length - m[2].length - 1, query: m[2].toLowerCase() }
}

/** The text with the mention at `start` replaced by `@token` and one space (none added when a
 *  space already follows), and where the caret goes. The rest of a word under the caret is part
 *  of the replaced mention. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  token: string
): { text: string; caret: number } {
  const after = text.slice(caret)
  const tail = /^[a-z0-9_.-]*/i.exec(after)?.[0] ?? ''
  const rest = after.slice(tail.length)
  const inserted = `@${token}${/^\s/.test(rest) ? '' : ' '}`
  return { text: text.slice(0, start) + inserted + rest, caret: start + inserted.length }
}

export interface MentionOption {
  /** Inserted after the `@`. */
  token: string
  label: string
  detail: string
  /** The avatar's letters. */
  initials: string
  team: boolean
  staff: boolean
}

function wordsStartWith(text: string, query: string): boolean {
  const lower = text.toLowerCase()
  return lower.startsWith(query) || lower.split(/[\s_.-]+/).some(word => word.startsWith(query))
}

/**
 * The picker's rows for what is typed after the `@`: the team entry first, then the members —
 * thread participants, then SoundGuys team profiles, then the rest, in DealHunt's order within
 * each — matching the handle or a word of the display name. Up to `max` rows.
 */
export function mentionOptions(
  mentionables: DiscussionMentionables | null | undefined,
  query: string,
  max = 8
): MentionOption[] {
  if (!mentionables) {
    return []
  }
  const q = query.toLowerCase()
  const rows: MentionOption[] = []
  const team = mentionables.team
  if (!q || wordsStartWith(team.token, q) || wordsStartWith(team.label, q)) {
    rows.push({
      token: team.token,
      label: team.label,
      detail: 'Asks the editors to weigh in',
      initials: 'SG',
      team: true,
      staff: false,
    })
  }
  const rank = (m: DiscussionMentionable): number => {
    if (m.in_thread) {
      return 0
    }
    return m.is_staff ? 1 : 2
  }
  const members = mentionables.members
    .map((m, i) => ({ m, i }))
    .filter(
      ({ m }) =>
        !q ||
        wordsStartWith(m.handle, q) ||
        wordsStartWith(m.handle_display, q) ||
        (typeof m.display_name === 'string' && wordsStartWith(m.display_name, q))
    )
    .sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i)
  for (const { m } of members) {
    const role = m.is_staff ? TEAM_LABEL : tierLabel(m.tier)
    const detail = m.in_thread ? `${role} · in this thread` : role
    const handleLabel = m.handle_display || m.handle
    // A member who shows their name (identity ticket §3.3): the row reads the name, with the
    // `@handle` it inserts leading the detail line — the token is still the handle.
    const name = typeof m.display_name === 'string' && m.display_name ? m.display_name : null
    rows.push({
      token: m.handle,
      label: name ?? handleLabel,
      detail: name ? `@${handleLabel} · ${detail}` : detail,
      initials: avatarInitials(handleLabel, name),
      team: false,
      staff: m.is_staff === true,
    })
  }
  return rows.slice(0, max)
}

// ── DealHunt's read ──────────────────────────────────────────────────────────

/** The proxy's path for a thread, with the app's trailing-slash rule (else every read pays a
 *  308). */
export function mentionablesApiPath(id: string, trailingSlash: boolean): string {
  return withSlash(
    `/api/community/discussions/${encodeURIComponent(id.toLowerCase())}/mentionables`,
    trailingSlash
  )
}

/** DealHunt's `/mentionables` answer, kept to the contract's fields: a member without a valid
 *  handle is dropped, at most `MENTIONABLES_MAX` members. Null when it is not that shape. */
export function mentionablesProjection(body: unknown): DiscussionMentionables | null {
  const raw = body as { members?: unknown } | null
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.members)) {
    return null
  }
  const members: DiscussionMentionable[] = []
  const seen = new Set<string>()
  for (const item of raw.members as unknown[]) {
    const m = item as Partial<Record<keyof DiscussionMentionable, unknown>> | null
    const handle = memberHandleParam(m?.handle)
    if (!m || !handle || seen.has(handle)) {
      continue
    }
    seen.add(handle)
    members.push({
      handle,
      handle_display:
        typeof m.handle_display === 'string' && m.handle_display.trim()
          ? m.handle_display.trim()
          : handle,
      tier: knownTier(typeof m.tier === 'string' ? m.tier : ''),
      ...(m.is_staff === true ? { is_staff: true as const } : {}),
      in_thread: m.in_thread === true,
    })
    if (members.length >= MENTIONABLES_MAX) {
      break
    }
  }
  return {
    // The team entry is SoundGuys copy (vocabulary rule: "SoundGuys team", never "staff").
    team: { token: TEAM_TOKEN, label: TEAM_LABEL },
    members,
  }
}
